import { useRef, useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getFunctionsResolved } from "../redux-store/functionReducers/functionActions";

export const useVariableTooltip = (editorRef, variableSources) => {
  const dispatch = useDispatch();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogPosition, setDialogPosition] = useState({ top: 0, left: 0 });
  const [selectedVariable, setSelectedVariable] = useState({
    key: "",
    value: "",
    type: "",
  });
  const hiddenSpanRef = useRef(null);
  const [variableKey, setVariableKey] = useState("");
  const [variableValue, setVariableValue] = useState("");
  const variableSourcesRef = useRef(variableSources);
  const [isResolvingFunction, setIsResolvingFunction] = useState(false);

  const debounceTimeoutRef = useRef(null);
  const lastHoveredFunction = useRef(null);
  
  // Cache for resolved functions
  const functionCache = useRef(new Map());
  const cacheKeyRef = useRef("");

  const selectedEnvironmentId = useSelector(
    (state) => state.variableSet.selectedEnvironmentVariables?.id
  );

  const selectedEntityId = useSelector(
    (state) => state.user.selectedSidebarState.id
  );

  const selectedTestCaseSetId = useSelector(
    (state) => state.testCaseSet.expandedTestCaseSetId
  );

  useEffect(() => {
    variableSourcesRef.current = variableSources;
  }, [variableSources]);

  // Update cache key when dependencies change
  useEffect(() => {
    const requestEntityId = selectedTestCaseSetId || selectedEntityId;
    const requestKey = selectedTestCaseSetId ? "testCaseSetId" : "entityId";
    cacheKeyRef.current = `${requestEntityId}_${selectedEnvironmentId}_${requestKey}`;
    
    // Clear cache when dependencies change
    functionCache.current.clear();
  }, [selectedEnvironmentId, selectedEntityId, selectedTestCaseSetId]);

  const setupHiddenSpan = () => {
    if (!hiddenSpanRef.current) {
      const hiddenSpan = document.createElement("span");
      hiddenSpan.style.visibility = "hidden";
      hiddenSpan.style.position = "absolute";
      hiddenSpan.style.whiteSpace = "pre";
      hiddenSpan.style.left = "-9999px";
      document.body.appendChild(hiddenSpan);
      hiddenSpanRef.current = hiddenSpan;
    }
  };

  const getValueByVarKey = (json, varKey) => {
    // Normalize the key: convert brackets to dots
    const normalizedKey = varKey
      .replace(/\[(\d+)\]/g, ".$1") // [0] => .0
      .replace(/\['(.+?)'\]/g, ".$1") // ['teams'] => .teams
      .replace(/\["(.+?)"\]/g, ".$1"); // ["teams"] => .teams

    const keys = normalizedKey.split(".").filter(Boolean); // split and remove empty strings

    return keys.reduce((obj, key) => obj && obj[key], json);
  };

  const resolveFunction = async (functionName) => {
    if (!selectedEnvironmentId || (!selectedEntityId && !selectedTestCaseSetId)) {
      return null;
    }

    // Check cache first
    const cacheKey = `${cacheKeyRef.current}_${functionName}`;
    if (functionCache.current.has(cacheKey)) {
      return functionCache.current.get(cacheKey);
    }

    try {
      setIsResolvingFunction(true);

      // Use testCaseSetId if available, otherwise use entityId
      const requestEntityId = selectedTestCaseSetId || selectedEntityId;
      const requestKey = selectedTestCaseSetId ? "testCaseSetId" : "entityId";

      const response = await dispatch(
        getFunctionsResolved({
          expression: functionName,
          [requestKey]: requestEntityId,
          environmentSetId: selectedEnvironmentId,
        })
      ).unwrap();

      const resolvedValue = response?.js_expression_value || null;

      // Cache the resolved value
      functionCache.current.set(cacheKey, resolvedValue);
      return resolvedValue;
    } catch (error) {
      console.error("Error resolving function:", error);
      // Cache null values too to avoid repeated failed requests
      functionCache.current.set(cacheKey, null);
      return null;
    } finally {
      setIsResolvingFunction(false);
    }
  };

  const handleMouseMove = useCallback(
    ({ clientX, clientY, variableName, event }) => {
      let leftPos = clientX + 10;
      let topPos = clientY - 20;
      let varKey = variableName;

      if (!variableName && event) {
        const input = event.target;
        const inputValue = input?.value;
        if (!inputValue) return;

        const rect = input.getBoundingClientRect();
        const x = event.clientX - rect.left;

        const variableMatches = [...inputValue.matchAll(/{{(.*?)}}/g)];
        if (variableMatches.length === 0) {
          setDialogOpen(false);
          return;
        }

        let closestVar = null;
        let closestDistance = Infinity;

        let currentOffset = 0;
        for (const match of variableMatches) {
          const [fullMatch, extractedVar] = match;
          const startIndex = inputValue.indexOf(fullMatch, currentOffset);
          const endIndex = startIndex + fullMatch.length;

          hiddenSpanRef.current.textContent = inputValue.substring(
            0,
            startIndex
          );
          const startX = hiddenSpanRef.current.offsetWidth;

          hiddenSpanRef.current.textContent = fullMatch;
          const varWidth = hiddenSpanRef.current.offsetWidth;
          const endX = startX + varWidth;

          if (x >= startX - 5 && x <= endX + 5) {
            const distance = Math.abs(x - (startX + varWidth / 2));
            if (distance < closestDistance) {
              closestVar = extractedVar.trim();
              closestDistance = distance;
            }
          }

          currentOffset = endIndex;
        }

        if (closestVar) {
          varKey = closestVar;
        } else {
          setDialogOpen(false);
          return;
        }
      }

      if (varKey) {
        let content = null;
        let variableType = null;

        // Use the ref to access the latest variableSources
        const currentSources = variableSourcesRef.current;

        if (currentSources?.environment?.[varKey] !== undefined) {
          variableType = "Environment";
          content = currentSources.environment[varKey];
        } else if (currentSources?.folder?.[varKey] !== undefined) {
          variableType = "Folder";
          content = currentSources.folder[varKey];
        } else if (currentSources?.collection?.[varKey] !== undefined) {
          variableType = "Collection";
          content = currentSources.collection[varKey];
        } else {
          const resolvedApiValue = getValueByVarKey(
            currentSources.apiResponseVariables,
            varKey
          );

          if (resolvedApiValue !== undefined) {
            variableType = "API Variables";
            content = resolvedApiValue;
          } else {
            // Check if this is actually a function call (contains parentheses)
            const isFunctionCall = varKey.includes('(') && varKey.includes(')');
            
            if (isFunctionCall) {
              // This is a function call, handle with optimized caching
              variableType = "Functions";

              // Check cache immediately first
              const cacheKey = `${cacheKeyRef.current}_${varKey}`;
              const cachedValue = functionCache.current.get(cacheKey);
              
              if (cachedValue !== undefined) {
                // Use cached value immediately
                content = cachedValue;
              } else {
                // Clear previous timeout
                if (debounceTimeoutRef.current) {
                  clearTimeout(debounceTimeoutRef.current);
                }

                // Update the last hovered function
                lastHoveredFunction.current = varKey;

                // Set a debounced API call for uncached functions
                debounceTimeoutRef.current = setTimeout(async () => {
                  const resolvedValue = await resolveFunction(varKey);
                  if (lastHoveredFunction.current === varKey) {
                    setSelectedVariable((prev) => ({
                      ...prev,
                      value: resolvedValue || "",
                    }));
                    setVariableValue(resolvedValue || "");
                  }
                }, 150); // Reduced debounce for better responsiveness
              }
            } else {
              // Not a function call, mark as unresolved
              variableType = "Unresolved";
              content = null;
            }
          }
        }

        setSelectedVariable({
          key: varKey,
          value: content || "",
          type: variableType || "Unresolved",
        });
        setVariableKey(varKey);
        setVariableValue(content || "");
        setDialogPosition({ left: leftPos, top: topPos });
        setDialogOpen(true);
      }
    },
    [dispatch, selectedEnvironmentId, selectedEntityId, selectedTestCaseSetId]
  );

  const handleClose = useCallback((e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    // Clear any pending debounced calls
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    lastHoveredFunction.current = null;
    setDialogOpen(false);
  }, []);

  useEffect(() => {
    setupHiddenSpan();

    return () => {
      if (hiddenSpanRef.current) {
        hiddenSpanRef.current.remove();
      }
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [editorRef]);

  return {
    handleMouseMove,
    dialogOpen,
    dialogPosition,
    selectedVariable,
    handleClose,
    setVariableKey,
    setVariableValue,
    variableKey,
    variableValue,
    isResolvingFunction,
  };
};
